import * as React from "react";
import * as S from "@styled";
import styled from "styled-components";
import Modal from 'react-modal';
import { device } from "shared/styles/helpers"
import theme from "styled-theming";
import { ThemeType } from "shared/styles/theme";
import colors from "shared/styles/colors";

const getWidth = (size: ModalSize | undefined, map: { [key in ModalSize]: string }) => {
    return `width: calc(100vw - ${map[size ?? "small"]});`
}


const SmallScreenModalSizes: { [key in ModalSize]: string } = {
    "large": "0%",
    "medium": "10%",
    "smaller": "15%",
    "small": "20%"
}

const LaptopModalSizes: { [key in ModalSize]: string } = {
    "large": "5%",
    "medium": "30%",
    "smaller": "50%",
    "small": "60%"
}
const LaptopLModalSizes: { [key in ModalSize]: string } = {
    "large": "15%",
    "medium": "40%",
    "smaller": "60%",
    "small": "70%"
}
const DesktopModalSizes: { [key in ModalSize]: string } = {
    "large": "25%",
    "medium": "50%",
    "smaller": "65%",
    "small": "75%"
}

const ModalBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
});

const ModalStyle = styled.div<IModalStyleProps>`
    margin-top: 75px;
    border-radius: 25px;
    overflow: visible;
    padding: 32px;
    background: ${ModalBackgroundColor};
    overflow-y: auto;
    height: fit-content;
    max-height: 90vh;
    &:focus {
        outline: none; // Removes black "border"
    }
    ${(props) => getWidth(props.size, SmallScreenModalSizes)};
    @media ${device.laptop} {
        ${(props) => getWidth(props.size, LaptopModalSizes)};
    }

    @media ${device.laptopL} {
        ${(props) => getWidth(props.size, LaptopLModalSizes)};
    }

    @media ${device.desktop} {
        ${(props) => getWidth(props.size, DesktopModalSizes)};
    }

`;

const OverlayStyle = styled.div`
    display: flex;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(88, 105, 108, 0.5);
    z-index: 9999; // Make modal overlay be above everything else than the toast
`;

const CustomModal: React.FC<React.PropsWithChildren<IModalProps>> = (props) => {
    React.useEffect(() => {
        document.body.style.overflow = props.show ? 'hidden' : 'auto';
    }, [props.show])


    React.useEffect(() => {
        return () => {
            document.body.style.overflow = 'auto';
        }
    }, [])
    const contentStyle: React.CSSProperties = { display: "flex", gap: "35px", flexDirection: "column", ...(props.contentStyle ?? {}) };
    return (
        <Modal
            className="_"
            overlayClassName="_"
            contentElement={(innerProps, children) => <ModalStyle size={props.size} {...innerProps}>{children}</ModalStyle>}
            overlayElement={(innerProps, contentElement) => <OverlayStyle {...innerProps}>{contentElement}</OverlayStyle>}
            id={props.label}
            isOpen={props.show}
            onRequestClose={props.onClose}
            contentLabel={props.label}
            shouldCloseOnOverlayClick={props.shouldCloseOnOverlayClick}
            appElement={document.getElementById("main") || undefined}
            style={{ content: contentStyle }}
        >
            <S.FlexContainer style={{ padding: "0px", justifyContent: `${props.modalTitle == null ? "flex-end" : "space-between"} `, alignItems: "center" }}>
                {props.modalTitle} {!props.hideCloseButton && <StyledCloseButton id="close-modal" onClick={props.onClose}><i className="ci-close_big" /></StyledCloseButton>}
            </S.FlexContainer>
            {/* height calcualtion is to remove the gap */}
            <S.FlexContainer asColumn={true} noWrap={props.noWrapContent} stretch={true} style={{ width: '100%', height: "calc(100% - 35px)" }}>
                {props.children}
            </S.FlexContainer>
        </Modal>
    )
}

export const StyledCloseButton = styled.button`
    all: unset;
    outline: revert;
    cursor: pointer;

    > i {
        font-size: 24px;
    }
`;

export interface IModalProps extends IModalStyleProps {
    label: string;
    shouldCloseOnOverlayClick: boolean;
    modalTitle?: React.ReactNode;
    show: boolean;
    contentStyle?: React.CSSProperties;
    noWrapContent?: boolean;
    hideCloseButton?: boolean;
    onClose?: (() => void);
}

export interface IModalStyleProps {
    size?: ModalSize
}
type ModalSize = "large" | "medium" | "smaller" | "small";

export default CustomModal;